import { create } from 'zustand';

import organizationService, {
    Organization,
    OrganizationUser,
    UpdateOrganizationData,
    OrganizationUserListParams,
    JoinRequestListParams,
} from '../services/organization.service';

export interface JoinRequest {
    _id: string;
    requestId: string;
    userId: string;
    organizationId: string;
    userEmail: string;
    userName: string;
    status: 'pending' | 'approved' | 'rejected';
    createdAt: string;
}

interface OrganizationState {
    organization: Organization | null;
    users: OrganizationUser[];
    joinRequests: JoinRequest[];
    total: number;
    loading: boolean;
    error: string | null;

    // Actions
    fetchOrganization: (orgId?: string) => Promise<void>;
    updateOrganization: (data: UpdateOrganizationData) => Promise<Organization>;
    fetchOrganizationUsers: (params?: OrganizationUserListParams) => Promise<void>;
    inviteUser: (email: string, role: string) => Promise<void>;
    removeUser: (userId: string) => Promise<void>;
    updateUserRole: (userId: string, role: string) => Promise<OrganizationUser>;
    fetchJoinRequests: (params?: JoinRequestListParams) => Promise<void>;
    approveJoinRequest: (requestId: string) => Promise<void>;
    rejectJoinRequest: (requestId: string, reason?: string) => Promise<void>;
    setCurrentOrganization: (org: Organization | null) => void;
    clearError: () => void;
}

export const useOrganizationStore = create<OrganizationState>((set, get) => ({
    organization: null,
    users: [],
    joinRequests: [],
    total: 0,
    loading: false,
    error: null,

    fetchOrganization: async (orgId) => {
        set({ loading: true, error: null });
        try {
            const organization = orgId
                ? await organizationService.getOrganizationById(orgId)
                : await organizationService.getCurrentOrganization();
            set({ organization, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    updateOrganization: async (data) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            const organization = await organizationService.updateOrganization(currentOrg._id, data);
            set({
                organization,
                loading: false,
            });
            return organization;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    fetchOrganizationUsers: async (params) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            const response = await organizationService.getOrganizationUsers(currentOrg._id, params);
            set({
                users: response.users,
                total: response.total,
                loading: false,
            });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    inviteUser: async (email, role) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            await organizationService.inviteUser(currentOrg._id, { email, role });
            set({ loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    removeUser: async (userId) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            await organizationService.removeUser(currentOrg._id, userId);
            set((state) => ({
                users: state.users.filter((u) => u._id !== userId),
                total: state.total - 1,
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    updateUserRole: async (userId, role) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            const user = await organizationService.updateUserRole(currentOrg._id, userId, role);
            set((state) => ({
                users: state.users.map((u) => (u._id === userId ? user : u)),
                loading: false,
            }));
            return user;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    fetchJoinRequests: async (params) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            const response = await organizationService.getJoinRequests(currentOrg._id, params);
            set({
                joinRequests: response.requests as any,
                total: response.total,
                loading: false,
            });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    approveJoinRequest: async (requestId) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            await organizationService.approveJoinRequest(currentOrg._id, requestId);
            set((state) => ({
                joinRequests: state.joinRequests.map((req) => (req._id === requestId ? { ...req, status: 'approved' as const } : req)),
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    rejectJoinRequest: async (requestId, reason) => {
        set({ loading: true, error: null });
        try {
            const currentOrg = get().organization;
            if (!currentOrg) {
                throw new Error('No organization set');
            }
            await organizationService.rejectJoinRequest(currentOrg._id, requestId, reason);
            set((state) => ({
                joinRequests: state.joinRequests.map((req) => (req._id === requestId ? { ...req, status: 'rejected' as const } : req)),
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    setCurrentOrganization: (org) => {
        set({ organization: org });
    },

    clearError: () => {
        set({ error: null });
    },
}));
